import { getActiveSession } from './active-session'
import { authClient, dataClient, sharedAuthClient, sharedSignInEnabled } from './supabase'

// Spoke side of the Navo24 cross-domain SSO hand-off (hub: https://trackingmcp.com/auth/hand-off).
// A visitor signed in on the hub is signed in here without a second login:
//   1. no session, no `#sso=` fragment, no `sso_tried` marker -> set the marker FIRST, redirect once to
//      the hub with `return` = this page's own URL (never any token);
//   2. `#sso=<nonce>` -> strip it from the address bar, redeem it ONCE (redeem_sso_handoff is
//      delete-on-read), setSession. The nonce is never logged or sent anywhere else;
//   3. no nonce, failed redemption or marker already set -> show the normal login, never redirect again.
// TWO Supabase projects here (see supabase.ts): the hub's nonce lives in the SHARED project, so it is
// redeemed with dataClient. With VITE_SHARED_SIGN_IN on, the session is adopted by sharedAuthClient (the
// same project, so setSession accepts it) and WINS over any old identity-project session, which stays
// untouched. With the flag off it is handed to authClient exactly as before, where the identity project
// refuses a shared token (the reason this was inert until sign-in moved).
// Standalone site only: main.tsx calls this, the federated remote (WorkspaceApp) never does, because the
// hub already holds the session there. Existing sessions are untouched (no storageKey/adapter change).

export const HUB_HAND_OFF_URL = 'https://trackingmcp.com/auth/hand-off'
export const SSO_MARKER_KEY = 'sso_tried'
const SSO_PARAM = 'sso'
// Apex only (the hub allowlist also accepts www, but this site never sets a www session apart from the apex). Anything else (localhost, previews) never redirects.
const FAMILY_HOSTS: ReadonlySet<string> = new Set(['freightratesmcp.com'])
// The first-visit hop belongs to the places where being signed in matters: the account page and the
// dashboard (the account page also exists under each locale prefix, /de/account, /zh-cn/account...).
// Every other route is public and indexable (home, rates, lanes, pricing, developers, book...): a
// first-time visitor there must see THIS site, never the hub's login page.
const SIGN_IN_ROUTE = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?(?:account|dashboard)\/?$/i
export const isSignInRoute = (pathname: string) => SIGN_IN_ROUTE.test(pathname)

const BOT_UA =
  /bot|crawl|spider|slurp|lighthouse|headless|prerender|pagespeed|facebookexternalhit|embedly|preview|monitor|curl|wget/i

export interface SsoInput {
  /** any session this tab can read (shared or old identity): blocks the first-visit redirect */
  hasSession: boolean
  /**
   * a session on the project the nonce signs into. Blocks redeeming. Omitted = same as hasSession (the
   * flag-off behaviour). With shared sign-in on, an old identity-only session does NOT block it: the hub
   * round trip was started on purpose and the shared session wins.
   */
  hasTargetSession?: boolean
  /**
   * Whether the first-visit hop to the hub may start. It only helps when a nonce can land in a working
   * session, i.e. with shared sign-in on. Off (the default today), the old identity project refuses a shared
   * token, so the hop would only send a signed-out visitor of /account (the sign-in page) to the hub login
   * for nothing. Omitted = enabled (pure-function default); the browser glue passes the flag.
   */
  hopEnabled?: boolean
  hash: string
  markerSet: boolean
  href: string
  embeddedInHub: boolean
  userAgent: string
  webdriver: boolean
}

export type SsoDecision =
  | { action: 'redeem'; nonce: string }
  | { action: 'redirect'; url: string }
  | { action: 'skip' }

/** The nonce carried by a `#sso=` fragment, or null. */
export function nonceFromHash(hash: string): string | null {
  const v = new URLSearchParams(hash.replace(/^#/, '')).get(SSO_PARAM)
  return v ? v : null
}

/** The fragment with `sso=` removed (other hash params kept), as `#a=b` or ''. */
export function hashWithoutNonce(hash: string): string {
  const p = new URLSearchParams(hash.replace(/^#/, ''))
  p.delete(SSO_PARAM)
  const rest = p.toString()
  return rest ? `#${rest}` : ''
}

/** The hub hand-off URL for a spoke page, or null when the page is not an allowlisted https family URL. */
export function buildHandOffUrl(href: string): string | null {
  let u: URL
  try {
    u = new URL(href)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' || u.port || u.username || u.password) return null
  if (!FAMILY_HOSTS.has(u.hostname)) return null
  // `return` is this page's own URL; a nonce can never ride in it (redirect only happens without one).
  if (nonceFromHash(u.hash) !== null) return null
  return `${HUB_HAND_OFF_URL}?return=${encodeURIComponent(u.href)}`
}

/** Pure decision: redeem a nonce, redirect once to the hub, or do nothing. */
export function decideSsoHandOff(i: SsoInput): SsoDecision {
  if (i.embeddedInHub) return { action: 'skip' }
  if (/(^|[#&])sb_session=/.test(i.hash)) return { action: 'skip' }
  const nonce = nonceFromHash(i.hash)
  if (nonce)
    return (i.hasTargetSession ?? i.hasSession) ? { action: 'skip' } : { action: 'redeem', nonce }
  if (i.hasSession || i.markerSet) return { action: 'skip' }
  if (i.webdriver || BOT_UA.test(i.userAgent)) return { action: 'skip' }
  if (i.hopEnabled === false) return { action: 'skip' }
  const url = buildHandOffUrl(i.href)
  if (!url) return { action: 'skip' }
  let pathname = ''
  try {
    pathname = new URL(i.href).pathname
  } catch {
    return { action: 'skip' }
  }
  return isSignInRoute(pathname) ? { action: 'redirect', url } : { action: 'skip' }
}

function readMarker(): boolean {
  try {
    return window.sessionStorage.getItem(SSO_MARKER_KEY) !== null
  } catch {
    // Storage blocked: we cannot prove "never redirect again", so treat as already tried.
    return true
  }
}

function setMarker(): boolean {
  try {
    window.sessionStorage.setItem(SSO_MARKER_KEY, '1')
    return window.sessionStorage.getItem(SSO_MARKER_KEY) === '1'
  } catch {
    return false
  }
}

function stripNonceFromUrl(): void {
  try {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + hashWithoutNonce(window.location.hash)
    )
  } catch {
    /* history blocked: the nonce is single-use and still redeemed below */
  }
}

/** Which sessions exist, read without side effects. Never throws. */
export async function readSessions(): Promise<{ any: boolean; target: boolean }> {
  try {
    const a = await getActiveSession()
    // Target = the project a redeemed nonce lands in: shared when the flag is on, else the identity project.
    return { any: !!a, target: sharedAuthClient ? a?.source === 'shared' : !!a }
  } catch {
    return { any: false, target: false }
  }
}

async function redeem(nonce: string): Promise<void> {
  try {
    const { data, error } = await dataClient.rpc('redeem_sso_handoff', { p_nonce: nonce })
    const row = Array.isArray(data) ? data[0] : null
    if (!error && row?.access_token && row?.refresh_token) {
      await (sharedAuthClient ?? authClient).auth.setSession({
        access_token: row.access_token,
        refresh_token: row.refresh_token,
      })
    }
  } catch {
    /* invalid / expired / used nonce: no retry, the visitor signs in normally */
  }
}

/** Run once at standalone boot. Awaits only when a nonce must be redeemed. */
export async function runSsoHandOff(): Promise<void> {
  if (typeof window === 'undefined') return
  const hash = window.location.hash
  const nonce = nonceFromHash(hash)
  if (nonce) {
    // Strip first, always, so the nonce never lingers in the bar/history.
    stripNonceFromUrl()
    // A nonce means the hub round trip happened: never start another one in this tab session.
    setMarker()
  }
  const sessions = await readSessions()
  const decision = decideSsoHandOff({
    hasSession: sessions.any,
    hasTargetSession: sessions.target,
    hopEnabled: sharedSignInEnabled,
    hash,
    markerSet: readMarker(),
    href: window.location.href,
    embeddedInHub: window.self !== window.top,
    userAgent: navigator.userAgent,
    webdriver: navigator.webdriver === true,
  })
  if (decision.action === 'redeem') {
    await redeem(decision.nonce)
  } else if (decision.action === 'redirect') {
    // Marker FIRST, then redirect. If it cannot be written the loop guard is unprovable: do not go.
    if (!setMarker()) return
    window.location.replace(decision.url)
  }
}
